import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import { BancaUtils, TipoBancaNome } from '../../model/banca/Banca';
import logoIdentifica from '../../assets/img/logo-color.png';
import logoDedh from '../../assets/img/logotipo_dedh.png';
import logoDetic from '../../assets/img/logotipo_detic.png';
import CandidatoAcessoViewModel, {
  PublicCandidatoChallenge,
  SalaProcessoCandidatoResponse,
} from '../../viewmodel/CandidatoAcessoViewModel';

type FormValues = {
  inscricao: string;
  documento: string;
};

const validationSchema = Yup.object({
  inscricao: Yup.string().trim().required('Inscrição é obrigatória.'),
  documento: Yup.string().trim().required('Documento é obrigatório.'),
});

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const sha256Hex = async (value: string): Promise<string> => {
  const data = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const resolverChallenge = async (challenge: PublicCandidatoChallenge): Promise<string> => {
  const prefixo = '0'.repeat(Math.max(1, challenge.dificuldade));
  let tentativa = 0;

  while (tentativa < Number.MAX_SAFE_INTEGER) {
    const hash = await sha256Hex(`${challenge.challengeId}:${challenge.nonce}:${tentativa}`);
    if (hash.startsWith(prefixo)) {
      return tentativa.toString();
    }

    tentativa += 1;
    if (tentativa % 2000 === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  throw new Error('Não foi possível resolver o desafio de segurança.');
};

const AcessoSalaCandidato: React.FC = () => {
  const [resultado, setResultado] = useState<SalaProcessoCandidatoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<PublicCandidatoChallenge | null>(null);
  const [challengeLoading, setChallengeLoading] = useState(true);
  const [showModal, setShowModal] = useState(true);
  const [inscricao, setInscricao] = useState('');
  const cardStyle = {
    borderRadius: 12,
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  } as const;
  const virtualCardStyle = {
    ...cardStyle,
    border: '1px solid #bfdbfe',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  } as const;
  const recursoCardStyle = {
    ...cardStyle,
    border: '1px solid #fcd34d',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  } as const;

  const carregarChallenge = async () => {
    setChallengeLoading(true);
    try {
      const challengeAtual = await CandidatoAcessoViewModel.obterChallenge();
      setChallenge(challengeAtual);
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Falha ao preparar validação de segurança.');
      setChallenge(null);
    } finally {
      setChallengeLoading(false);
    }
  };

  useEffect(() => {
    carregarChallenge();
  }, []);

  const hasDadosRecurso = (resultadoAtual: SalaProcessoCandidatoResponse | null): boolean => {
    if (!resultadoAtual) {
      return false;
    }
    return Boolean(
      resultadoAtual.recursoDisponivel ||
      resultadoAtual.recursoJaPreenchido ||
      resultadoAtual.recursoUrl ||
      resultadoAtual.mensagemRecurso ||
      resultadoAtual.recursoEditalNome ||
      resultadoAtual.dataHoraBancaRecurso
    );
  };

  const handleSubmit = async (values: FormValues) => {
    if (!challenge) {
      toast.error('Validação de segurança indisponível. Tente novamente em alguns segundos.');
      return;
    }

    setLoading(true);
    setResultado(null);

    try {
      const solution = await resolverChallenge(challenge);
      const response = await CandidatoAcessoViewModel.consultarSalaProcesso(
        values.inscricao,
        values.documento,
        challenge.challengeId,
        solution
      );

      setResultado(response);
      setInscricao(values.inscricao);
      setShowModal(false);

      if (response.acessoSalaVirtualLiberado) {
        toast.success('URL da sala liberada.');
      } else {
        toast.info(response.mensagemSalaVirtual);
      }
    } catch (error) {
      toast.error(BackendErrorMessage(error) || 'Não foi possível validar os dados informados.');
    } finally {
      setLoading(false);
      await carregarChallenge();
    }
  };

  return (
    <Container className="mt-4 mb-5">
      <Modal show={showModal} onHide={() => {}} backdrop="static" centered>
        <Modal.Header>
          <Modal.Title>Identificação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Informe sua inscrição e documento (CPF, RNE ou passaporte).
          </p>
          <Formik<FormValues>
            initialValues={{ inscricao: '', documento: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange }) => (
              <FormikForm>
                <Form.Group className="mb-3">
                  <Form.Label>Inscrição</Form.Label>
                  <Form.Control
                    name="inscricao"
                    value={values.inscricao}
                    onChange={handleChange}
                    isInvalid={Boolean(touched.inscricao && errors.inscricao)}
                  />
                  <Form.Control.Feedback type="invalid">{errors.inscricao}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>CPF, RNE ou Passaporte</Form.Label>
                  <Form.Control
                    name="documento"
                    value={values.documento}
                    onChange={handleChange}
                    isInvalid={Boolean(touched.documento && errors.documento)}
                  />
                  <Form.Control.Feedback type="invalid">{errors.documento}</Form.Control.Feedback>
                </Form.Group>

                <div className="d-grid">
                  <Button type="submit" disabled={loading || challengeLoading || !challenge}>
                    {loading ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Validando...
                      </>
                    ) : (
                      'Consultar'
                    )}
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      <Row className="justify-content-center mb-3">
        <Col md={10} lg={9}>
          <div
            style={{
              borderRadius: 12,
              padding: '1rem 1.25rem',
              background: '#ffffff',
              border: '1px solid #e8edf2',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
            }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-2">
                <img src={logoIdentifica} alt="Identifica" style={{ height: 34, width: 'auto' }} />
                <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '1.1rem' }}>Identifica</div>
              </div>
              <div
                className="d-flex align-items-center gap-2"
                style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '0.75rem' }}
              >
                <img src={logoDedh} alt="DeDH" style={{ height: 30, width: 'auto' }} />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="justify-content-center mb-3">
        <Col md={10} lg={9}>
          <div
            style={{
              borderRadius: 12,
              padding: '1.1rem 1.25rem',
              background: '#ffffff',
              border: '1px solid #e8edf2',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          >
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1f2937' }}>Acesso do Candidato</div>
          </div>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={10} lg={9}>
          {inscricao && (
            <div
              className="mb-3 px-3 py-2"
              style={{ backgroundColor: '#f8f9fa', borderRadius: 10, fontSize: '0.9rem', color: '#334155', border: '1px solid #eef2f6', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}
            >
              <strong>Inscrição:</strong> {inscricao}
            </div>
          )}

          {resultado && (
            <Row className="mt-3 g-3">
              <Col xs={12}>
                <Alert className="mb-0" variant={resultado.acessoSalaVirtualLiberado ? 'success' : 'warning'} style={virtualCardStyle}>
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                    {BancaUtils.getIconeBanca(TipoBancaNome.VIRTUAL)}
                    <span>Banca Virtual</span>
                  </div>
                  {!resultado.acessoSalaVirtualLiberado && <div>{resultado.mensagemSalaVirtual}</div>}
                  {resultado.acessoSalaVirtualLiberado && resultado.salaVirtualUrl && (
                    <div className="mt-3 p-3" style={{ backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                      <strong>Sala</strong>
                      <div>{resultado.mensagemSalaVirtual}</div>
                      {resultado.salaVirtualEditalNome && (
                        <div>
                          <strong>Edital:</strong> {resultado.salaVirtualEditalNome}
                          {resultado.salaVirtualNumeroChamada !== null && resultado.salaVirtualNumeroChamada !== undefined
                            ? ` - Chamada ${resultado.salaVirtualNumeroChamada}`
                            : ''}
                        </div>
                      )}
                      {resultado.dataHoraBancaVirtual && (
                        <div>
                          <strong>Data/hora:</strong> {formatDateTime(resultado.dataHoraBancaVirtual)}
                        </div>
                      )}
                      <div className="mt-2">
                        <a
                          href={resultado.salaVirtualUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            fontSize: '0.875rem',
                            wordBreak: 'break-all',
                            color: '#0b5ed7',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          {resultado.salaVirtualUrl}
                        </a>
                      </div>
                    </div>
                  )}
                  {!resultado.acessoSalaVirtualLiberado && (
                    <>
                      {resultado.salaVirtualEditalNome && (
                        <div>
                          <strong>Edital:</strong> {resultado.salaVirtualEditalNome}
                          {resultado.salaVirtualNumeroChamada !== null && resultado.salaVirtualNumeroChamada !== undefined
                            ? ` - Chamada ${resultado.salaVirtualNumeroChamada}`
                            : ''}
                        </div>
                      )}
                      {resultado.dataHoraBancaVirtual && (
                        <div>
                          <strong>Data/hora:</strong> {formatDateTime(resultado.dataHoraBancaVirtual)}
                        </div>
                      )}
                      {resultado.salaVirtualLiberadoEm && (
                        <div>
                          <strong>Liberação:</strong> {formatDateTime(resultado.salaVirtualLiberadoEm)}
                        </div>
                      )}
                    </>
                  )}
                </Alert>
              </Col>

              <Col xs={12}>
                <Alert className="mb-0" variant={hasDadosRecurso(resultado) ? 'success' : 'secondary'} style={recursoCardStyle}>
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                    {BancaUtils.getIconeBanca(TipoBancaNome.RECURSAL)}
                    <span>Banca Recursal</span>
                  </div>
                  {!hasDadosRecurso(resultado) && <div>Sem informações de recurso para o momento.</div>}
                  {hasDadosRecurso(resultado) && (
                    <div className="mt-3 p-3" style={{ backgroundColor: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                      <strong>Recurso</strong>
                      <div>
                        <strong>Status:</strong> {resultado.mensagemRecurso || 'Sem informações de recurso.'}
                      </div>
                      {resultado.recursoEditalNome && (
                        <div>
                          <strong>Edital:</strong> {resultado.recursoEditalNome}
                          {resultado.recursoNumeroChamada !== null && resultado.recursoNumeroChamada !== undefined
                            ? ` - Chamada ${resultado.recursoNumeroChamada}`
                            : ''}
                        </div>
                      )}
                      {resultado.dataHoraBancaRecurso && (
                        <div>
                          <strong>Origem:</strong> Banca Recursal em {formatDateTime(resultado.dataHoraBancaRecurso)}
                        </div>
                      )}
                      {resultado.recursoDisponivel && resultado.recursoUrl && (
                        <div className="mt-2">
                          <a
                            href={resultado.recursoUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              fontSize: '0.875rem',
                              wordBreak: 'break-all',
                              color: '#0b5ed7',
                              fontWeight: 600,
                              textDecoration: 'none',
                            }}
                          >
                            {resultado.recursoUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </Alert>
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      <Row className="justify-content-center mt-4">
        <Col md={10} lg={9}>
          <div className="text-center pt-3" style={{ borderTop: '1px solid #e8edf2' }}>
            <div style={{ color: '#94a3b8', fontSize: '0.82rem' }}>Desenvolvido por</div>
            <a href="https://detic.unicamp.br/" target="_blank" rel="noreferrer" className="d-inline-block mt-1">
              <img src={logoDetic} alt="DETIC" style={{ height: 24, width: 'auto' }} />
            </a>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AcessoSalaCandidato;
